import { Fragment } from "react";
import type { ReactElement } from "react";
import { Route } from "react-router-dom";

import { LastYearsMapFilter } from "../../pages/lastYearsMapFilter";
import { ConfigDefinedMapPage } from "../../pages/mapPages/configDefinedMapPage";
import type { ConfigMapDefinition } from "./configMapDefinitions";
import {
    getRelativeMapFilterRoutePath,
    getRelativeMapRoutePath,
} from "./configMapDefinitions";

type ConfigMapRouteDescriptor = {
    key: string;
    path: string;
    element: ReactElement;
};

type ConfigMapRouteFactory = (
    definition: ConfigMapDefinition
) => ConfigMapRouteDescriptor | undefined;

function buildConfigMapPageRoute(definition: ConfigMapDefinition) {
    const definitionId = definition?.id;
    const relativeRoutePath = getRelativeMapRoutePath(definition?.url);

    if (!definitionId || !relativeRoutePath) return undefined;

    return {
        key: `config-map-page-${definitionId}`,
        path: relativeRoutePath,
        element: <ConfigDefinedMapPage definitionId={definitionId} />,
    };
}

function buildConfigMapLastYearsFilterRoute(definition: ConfigMapDefinition) {
    const definitionId = definition?.id;
    const relativeFilterRoutePath = getRelativeMapFilterRoutePath(definition?.url);
    const lastYearsFilter = definition?.lastYearsFilter;

    if (!definitionId || !lastYearsFilter || !relativeFilterRoutePath) return undefined;

    return {
        key: `config-map-filter-last-years-${definitionId}`,
        path: relativeFilterRoutePath,
        element: (
            <LastYearsMapFilter
                filterType="page"
                numberOfYears={lastYearsFilter?.numberOfYears ?? 5}
                objectType={definitionId}
                pagePrompt={lastYearsFilter?.pagePrompt}
            />
        ),
    };
}

const configMapRouteFactories: ConfigMapRouteFactory[] = [
    buildConfigMapPageRoute,
    buildConfigMapLastYearsFilterRoute,
];

function isConfigMapRouteDescriptor(
    route: ConfigMapRouteDescriptor | undefined
): route is ConfigMapRouteDescriptor {
    return Boolean(route);
}

export function renderConfigMapRoutes(definitions: ConfigMapDefinition[]) {
    return definitions.map((definition) => {
        const routes = configMapRouteFactories
            .map((factory) => factory(definition))
            .filter(isConfigMapRouteDescriptor);

        if (routes.length === 0) return null;

        return (
            <Fragment key={`config-map-${definition?.id ?? routes[0].path}`}>
                {routes.map(({ key, path, element }) => (
                    <Route key={key} path={path} element={element} />
                ))}
            </Fragment>
        );
    });
}
